import React from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Backpack, Gamepad2, Gem, ShoppingCart, User, Users, Wallet } from 'lucide-react'
import useAudioManager from '../hooks/useAudioManager'
import useAppStore from '../store/useAppStore'
import { getDesktopGameModeNavigationItems } from '../config/navigationConfig'
import { getAvailableWithdrawMethods } from '../features/withdraw'
import { LANGUAGE_OPTIONS } from '../i18n/config'
import { LOGO_SRC } from '../config/brand'

const DesktopNavigation = () => {
  const { t, i18n } = useTranslation()
  const { isGuest, language, changeLanguage, openLogin } = useAppStore((state) => ({
    isGuest: state.isGuest,
    language: state.language,
    changeLanguage: state.changeLanguage,
    openLogin: state.openLogin
  }))
  const balance = useAppStore((state) => state.balance)
  const user = useAppStore((state) => state.user)
  const { playSound } = useAudioManager({
    hover: 'hover',
    pick: 'pick'
  })

  const selectedLanguage = LANGUAGE_OPTIONS.find(({ code }) => code === language) || LANGUAGE_OPTIONS[0]
  const gameModeItems = getDesktopGameModeNavigationItems(t, user)

  const handleNavClick = () => {
    try {
      playSound('pick')

      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('heavy')
      }
    } catch (error) {
      // Игнорируем ошибки вибрации
    }
  }

  const handleNavHover = () => {
    playSound('hover')
  }

  if (isGuest) return (
    <header className="z-40">
      <div className="mx-auto w-full max-w-screen-xl px-6">
        <div className="space-y-4 p-4 lg:px-6 lg:py-5">
          <div className="flex items-center justify-between gap-6">
            <NavLink to="/" onClick={handleNavClick} className="shrink-0">
              <div className="relative inline-block">
                <img src={LOGO_SRC} alt="SKINOZ" className="relative z-10 h-auto w-[180px]" />
                <div className="pointer-events-none absolute left-1/2 top-full h-5 w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-primary/25 blur-xl" />
              </div>
            </NavLink>
            <nav className="flex items-center gap-2">
              {[
                { path: '/games', label: t('nav.games'), icon: Gamepad2 },
                { path: '/teams', label: t('guest.teams'), icon: Users },
                { path: '/market', label: t('market.title'), icon: ShoppingCart }
              ].map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  onClick={handleNavClick}
                  onMouseEnter={handleNavHover}
                  className={({ isActive }) => classNames(
                    'flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition-all duration-100',
                    isActive
                      ? 'border-accent-primary/30 bg-accent-primary/12 text-accent-primary shadow-[0_0_20px_rgba(0,255,127,0.1)]'
                      : 'border-white/10 bg-white/5 text-text-secondary hover:text-white'
                  )}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                </NavLink>
              ))}
              <div className="relative flex shrink-0 items-center gap-2 rounded-2xl border border-white/10 bg-surface px-3 py-3 text-sm text-white focus-within:border-accent-primary">
                <img
                  key={language}
                  src={`https://flagcdn.com/${selectedLanguage.country}.svg`}
                  alt=""
                  aria-hidden="true"
                  onError={(event) => { event.currentTarget.style.display = 'none' }}
                  className="pointer-events-none w-5 shrink-0 rounded-[3px] border border-white/20"
                />
                <span aria-hidden="true" className="whitespace-nowrap">{selectedLanguage.name}</span>
                <select value={language} onChange={(event) => changeLanguage(event.target.value, i18n.changeLanguage)} aria-label={t('guest.language')} className="absolute inset-0 h-full w-full cursor-pointer opacity-0">
                  {LANGUAGE_OPTIONS.map(({ code, name }) => <option key={code} value={code}>{name}</option>)}
                </select>
              </div>
              <button type="button" onClick={openLogin} className="rounded-2xl bg-gradient-to-r from-accent-primary to-accent-secondary px-5 py-3 text-sm font-semibold text-dark-primary transition-opacity hover:opacity-90">{t('guest.login')}</button>
            </nav>
          </div>
          <div className="flex justify-center">
            <nav className="flex w-max max-w-[calc(100vw-2rem)] shrink-0 items-center gap-3 overflow-x-auto rounded-[24px] border border-white/10 bg-dark-primary/40 p-2">
              {gameModeItems.map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    onMouseEnter={handleNavHover}
                    className={({ isActive }) => classNames(
                      'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-100',
                      isActive
                        ? 'bg-accent-primary/15 text-accent-primary shadow-[0_0_24px_rgba(0,255,127,0.12)]'
                        : 'text-text-secondary hover:bg-white/5 hover:text-white'
                    )}
                  >
                    <Icon size={18} />
                    <span>{item.title}</span>
                  </NavLink>
                )
              })}
            </nav>
          </div>
        </div>
      </div>
    </header>
  )

  return (
    <header className="z-40">
      <div className="mx-auto w-full max-w-screen-xl px-6">
        <div className="space-y-4  p-4 lg:px-6 lg:py-5">
          <div className="flex items-center justify-between gap-6">
            <NavLink to="/" onClick={handleNavClick} className="shrink-0">
              <div className="relative inline-block">
                <img
                  src={LOGO_SRC}
                  alt="SKINOZ"
                  className="relative z-10 h-auto w-[180px]"
                />
                <div className="pointer-events-none absolute left-1/2 top-full h-5 w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-primary/25 blur-xl" />
              </div>
            </NavLink>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-accent-primary">
                <Gem size={16} />
                <span>{balance.toLocaleString()}</span>
              </div>

              <NavLink
                to="/payment"
                onClick={handleNavClick}
                onMouseEnter={handleNavHover}
                className="rounded-2xl bg-gradient-to-r from-accent-primary to-accent-secondary px-5 py-3 text-sm font-semibold text-dark-primary transition-opacity duration-100 hover:opacity-90"
              >
                {t('home.topUp')}
                {user?.best_promo?.percent > 0 && (
                  <span className="ml-1 font-bold opacity-80">
                    +{user.best_promo.percent}%
                  </span>
                )}
              </NavLink>

              {getAvailableWithdrawMethods(user).length > 0 && (
                <NavLink
                  to="/withdraw"
                  onClick={handleNavClick}
                  onMouseEnter={handleNavHover}
                  className={({ isActive }) =>
                    classNames(
                      'flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition-all duration-100',
                      {
                        'border-accent-primary/30 bg-accent-primary/12 text-accent-primary shadow-[0_0_20px_rgba(0,255,127,0.1)]': isActive,
                        'border-white/10 bg-white/5 text-text-secondary hover:text-white': !isActive
                      }
                    )
                  }
                >
                  <Wallet size={16} />
                  <span>{t('home.withdraw', 'Вывести')}</span>
                </NavLink>
              )}

              <NavLink
                to="/market"
                onClick={handleNavClick}
                onMouseEnter={handleNavHover}
                className={({ isActive }) =>
                  classNames(
                    'flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition-all duration-100',
                    {
                      'border-accent-primary/30 bg-accent-primary/12 text-accent-primary shadow-[0_0_20px_rgba(0,255,127,0.1)]': isActive,
                      'border-white/10 bg-white/5 text-text-secondary hover:text-white': !isActive
                    }
                  )
                }
              >
                <ShoppingCart size={16} />
                <span>{t('market.title')}</span>
              </NavLink>

              <NavLink
                to="/inventory"
                onClick={handleNavClick}
                onMouseEnter={handleNavHover}
                className={({ isActive }) =>
                  classNames(
                    'flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition-all duration-100',
                    {
                      'border-accent-primary/30 bg-accent-primary/12 text-accent-primary shadow-[0_0_20px_rgba(0,255,127,0.1)]': isActive,
                      'border-white/10 bg-white/5 text-text-secondary hover:text-white': !isActive
                    }
                  )
                }
              >
                <Backpack size={16} />
                <span>{t('nav.inventory')}</span>
              </NavLink>

              <NavLink
                to="/profile"
                onClick={handleNavClick}
                onMouseEnter={handleNavHover}
                className={({ isActive }) =>
                  classNames(
                    'flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition-all duration-100',
                    {
                      'border-accent-primary/30 bg-accent-primary/12 text-accent-primary shadow-[0_0_20px_rgba(0,255,127,0.1)]': isActive,
                      'border-white/10 bg-white/5 text-text-secondary hover:text-white': !isActive
                    }
                  )
                }
              >
                <User size={16} />
                <span>{t('nav.profile')}</span>
              </NavLink>
            </div>
          </div>

          <div className="flex justify-center">
            <nav className="flex w-max max-w-[calc(100vw-2rem)] shrink-0 items-center gap-3 overflow-x-auto rounded-[24px] border border-white/10 bg-dark-primary/40 p-2">
              {gameModeItems.map((item) => {
                const Icon = item.icon

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    onMouseEnter={handleNavHover}
                    className={({ isActive }) =>
                      classNames(
                        'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-100',
                        {
                          'bg-accent-primary/15 text-accent-primary shadow-[0_0_24px_rgba(0,255,127,0.12)]': isActive,
                          'text-text-secondary hover:bg-white/5 hover:text-white': !isActive
                        }
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          size={18}
                          className={classNames({
                            'drop-shadow-[0_0_8px_rgba(0,255,127,0.6)]': isActive
                          })}
                        />
                        <span>{item.title}</span>
                      </>
                    )}
                  </NavLink>
                )
              })}
            </nav>
          </div>
        </div>
      </div>
    </header>
  )
}

export default DesktopNavigation
